'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import FileUploadZone from './FileUploadZone';
import MaterialSelector from './MaterialSelector';
import ProjectDetails from './ProjectDetails';
import QuoteSummary from './QuoteSummary';

export interface QuoteFormData {
  // Step 1: Project Info
  projectName: string;
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  
  // Step 2: Files
  files: File[];
  
  // Step 3: Material & Specs
  material: string;
  materialGrade: string;
  thickness: string;
  quantity: string;
  
  // Step 4: Additional Details
  finishType: string;
  toleranceRequirements: string;
  deliveryTimeline: string;
  specialRequirements: string;
}

const steps = [
  { id: 1, name: 'Project Info', icon: 'InformationCircleIcon' },
  { id: 2, name: 'Upload Files', icon: 'DocumentArrowUpIcon' },
  { id: 3, name: 'Material & Specs', icon: 'CubeIcon' },
  { id: 4, name: 'Review & Submit', icon: 'CheckCircleIcon' },
];

export default function SmartQuoteForm() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<QuoteFormData>({
    projectName: '',
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    files: [],
    material: '',
    materialGrade: '',
    thickness: '',
    quantity: '',
    finishType: '',
    toleranceRequirements: '',
    deliveryTimeline: '',
    specialRequirements: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const updateFormData = (updates: Partial<QuoteFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    // In production, this would send data to backend
    console.log('Quote submitted:', formData);
    setIsSubmitted(true);
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.projectName && formData.contactName && formData.email && formData.phone;
      case 2:
        return formData.files.length > 0;
      case 3:
        return formData.material && formData.materialGrade && formData.thickness && formData.quantity;
      case 4:
        return true;
      default:
        return false;
    }
  };

  if (isSubmitted) {
    return (
      <section className="py-14 lg:py-22 bg-gradient-to-br from-primary via-brand-primary to-secondary min-h-screen flex items-center">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl p-8 lg:p-12 text-center">
            <div className="w-20 h-20 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircleIcon" size={48} variant="solid" className="text-success" />
            </div>
            <h2 className="text-3xl font-headline font-bold text-foreground mb-4">
              Quote Request Submitted Successfully!
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Thank you for your quote request. Our technical team will review your specifications and respond within 24-48 hours.
            </p>
            <div className="bg-muted rounded-xl p-6 mb-8">
              <div className="grid grid-cols-2 gap-6">
                <div className="text-left">
                  <div className="text-sm text-muted-foreground mb-1">Project Name</div>
                  <div className="font-cta font-semibold text-foreground">{formData.projectName}</div>
                </div>
                <div className="text-left">
                  <div className="text-sm text-muted-foreground mb-1">Contact Email</div>
                  <div className="font-cta font-semibold text-foreground">{formData.email}</div>
                </div>
                <div className="text-left">
                  <div className="text-sm text-muted-foreground mb-1">Material</div>
                  <div className="font-cta font-semibold text-foreground">{formData.material} {formData.materialGrade}</div>
                </div>
                <div className="text-left">
                  <div className="text-sm text-muted-foreground mb-1">Quantity</div>
                  <div className="font-cta font-semibold text-foreground">{formData.quantity} units</div>
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                setIsSubmitted(false);
                setCurrentStep(1);
                setFormData({
                  projectName: '',
                  companyName: '',
                  contactName: '',
                  email: '',
                  phone: '',
                  files: [],
                  material: '',
                  materialGrade: '',
                  thickness: '',
                  quantity: '',
                  finishType: '',
                  toleranceRequirements: '',
                  deliveryTimeline: '',
                  specialRequirements: '',
                });
              }}
              className="px-8 py-3 bg-primary text-primary-foreground rounded-lg font-cta font-bold hover:opacity-90 transition-all duration-base"
            >
              Submit Another Quote
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-14 lg:py-22 bg-gradient-to-br from-primary via-brand-primary to-secondary">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          {/* FFC Logo */}
          <div className="flex justify-center mb-6">
            <AppImage
              src="/assets/images/FFC_logo_1-1769757459737.png"
              alt="Fusion Finishing Company Logo"
              width={200}
              height={80}
              className="h-16 w-auto"
            />
          </div>
          <h1 className="text-4xl lg:text-5xl font-headline font-bold text-white mb-4">
            Smart Quote Portal
          </h1>
          <p className="text-lg text-white/90 font-body">
            Get an accurate quote in minutes. Our intelligent form guides you through the process step-by-step.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-base ${
                      currentStep >= step.id
                        ? 'bg-white text-primary' :'bg-white/20 text-white/60'
                    }`}
                  >
                    <Icon name={step.icon as any} size={24} variant="outline" />
                  </div>
                  <div
                    className={`mt-2 text-sm font-cta font-semibold transition-all duration-base ${
                      currentStep >= step.id ? 'text-white' : 'text-white/60'
                    }`}
                  >
                    {step.name}
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`h-1 flex-1 mx-4 transition-all duration-base ${
                      currentStep > step.id ? 'bg-white' : 'bg-white/20'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl p-8 lg:p-12">
          {currentStep === 1 && (
            <ProjectDetails formData={formData} updateFormData={updateFormData} />
          )}
          {currentStep === 2 && (
            <FileUploadZone formData={formData} updateFormData={updateFormData} />
          )}
          {currentStep === 3 && (
            <MaterialSelector formData={formData} updateFormData={updateFormData} />
          )}
          {currentStep === 4 && (
            <QuoteSummary formData={formData} />
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-8 border-t border-border">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className="flex items-center space-x-2 px-6 py-3 bg-muted text-foreground rounded-lg font-cta font-semibold hover:bg-muted/80 transition-all duration-base disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name="ChevronLeftIcon" size={20} variant="outline" />
              <span>Previous</span>
            </button>

            {currentStep < steps.length ? (
              <button
                onClick={handleNext}
                disabled={!isStepValid()}
                className="flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-cta font-semibold hover:opacity-90 transition-all duration-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>Next Step</span>
                <Icon name="ChevronRightIcon" size={20} variant="outline" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                className="flex items-center space-x-2 px-8 py-3 bg-success text-white rounded-lg font-cta font-bold hover:opacity-90 transition-all duration-base shadow-lg"
              >
                <Icon name="PaperAirplaneIcon" size={20} variant="solid" />
                <span>Submit Quote Request</span>
              </button>
            )}
          </div>
        </div>

        {/* Help Section */}
        <div className="max-w-4xl mx-auto mt-8 bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Icon name="QuestionMarkCircleIcon" size={24} variant="outline" className="text-white" />
            </div>
            <div className="space-y-2">
              <div className="text-base font-cta font-bold text-white">
                Need Help?
              </div>
              <div className="text-sm text-white/90 font-body">
                Our technical team is available to assist you. Call us at (555) 123-4567 or email quotes@precisionworks.com
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}